import { useMainNavigationStore } from '@centric-os/stores/navigation/use-main-navigation-store';
import { useSplitIoStore } from '@centric-os/stores/splitio/use-split-io';
import { FeatureFlags } from '@centric-os/types';
import { SitesRouteNames } from '@centric-os/sites/src/enums';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { LoyaltyRouteNames } from '../../enums/router-names';

type LoyaltyAccess = 'allow' | 'deny' | 'abort';

/**
 * Shared loyalty access gate used by route `beforeEnter` guards.
 * `isFeatureOn` is a plain getter over the fetched flags, so reading it before
 * the fetch yields undefined and bounces the user to the sites list —
 * including a real user deep-linking or refreshing a loyalty URL.
 * `fetchFeatureFlags` is idempotent and de-duplicates concurrent callers.
 * Mirrors the discover module's guard.
 */
async function resolveLoyaltyAccess(): Promise<LoyaltyAccess> {
  const navStore = useMainNavigationStore();
  const splitIoStore = useSplitIoStore();

  try {
    if (!splitIoStore.initialized) {
      await splitIoStore.fetchFeatureFlags();
    }

    const isEnabled = splitIoStore.isFeatureOn(FeatureFlags.VOUCHERIFY_REPLACEMENT);
    return isEnabled && navStore.canAccessLoyalty ? 'allow' : 'deny';
  } catch (error) {
    console.error('Error during navigation guard execution:', error);
    return 'abort';
  }
}

export const beforeEnter = async (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const access = await resolveLoyaltyAccess();
  if (access === 'allow') {
    next();
    return;
  }
  if (access === 'deny') {
    next({ name: SitesRouteNames.SITES_LIST, replace: true });
    return;
  }
  next(false);
};

/**
 * Bundles are out of promo production scope (CGE-1286). Keep routes registered
 * so components stay in the tree for a later restore, but bounce deep links to
 * the campaigns list after the usual loyalty access check.
 */
export const beforeEnterBundlesHidden = async (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const access = await resolveLoyaltyAccess();
  if (access === 'allow') {
    next({ name: LoyaltyRouteNames.CAMPAIGNS_LIST, replace: true });
    return;
  }
  if (access === 'deny') {
    next({ name: SitesRouteNames.SITES_LIST, replace: true });
    return;
  }
  next(false);
};
